
import { defineComponent, PropType } from 'vue'

import { RepoPerms, TeamPerms } from '~/types/permTypes'
import { TeamMemberRoleChoices } from '~/types/types'

import OwnerDetailMixin from '~/mixins/ownerDetailMixin'
import RepoDetailMixin from '~/mixins/repoDetailMixin'
import RoleAccessMixin from '~/mixins/roleAccessMixin'

export default defineComponent({
  name: 'RepoInactive',
  mixins: [RepoDetailMixin, RoleAccessMixin, OwnerDetailMixin],

  props: {
    activateAnalysisLoading: {
      type: Boolean as PropType<boolean>,
      default: false
    }
  },
  computed: {
    canViewerUpgrade(): boolean {
      return this.$gateKeeper.team(TeamPerms.CHANGE_PLAN, this.teamPerms.permission)
    },
    configNotAdded(): boolean {
      return this.repository.errorCode === 3001
    },
    isConfigFileModeEnabled(): boolean {
      const ownerFromStore = this.$store.state.owner?.detail?.owner
      return ownerFromStore?.ownerSetting?.shouldCommitConfig === true
    },
    hasConfig(): boolean {
      // Resolved server-side so the dashboard and the analysis pipeline agree on
      // what an empty config means, rather than this component keeping its own
      // copy of that rule.
      return this.repository.hasConfig === true
    },
    shouldShowActivateButton(): boolean {
      // Show "Activate repository" when there is something to activate against:
      // 1. Config file already exists (!configNotAdded), AND
      // 2. either the team commits a .deepsource.toml, or the repository already
      //    has a stored config.
      // Without the second arm a UI-mode repo that already has a config is told
      // to generate one it does not need, with no way to just activate.
      return !this.configNotAdded && (this.isConfigFileModeEnabled || this.hasConfig)
    },
    noticeTitle(): string {
      if (!this.canActivateRepo) {
        return 'This repository is not activated'
      }

      if (this.repository.canBeActivated || this.repository.isActivated) {
        return 'Activate analysis on this repository'
      }

      if (this.canViewerUpgrade) {
        return 'Upgrade to activate this repository'
      }

      return 'Repository cannot be activated'
    },
    canActivateRepo(): boolean {
      const role = this.activeDashboardContext.role as TeamMemberRoleChoices
      return (
        this.$gateKeeper.team(TeamPerms.ACTIVATE_ANALYSIS, role) ||
        this.$gateKeeper.repo(RepoPerms.ACTIVATE_REPOSITORY, this.repoPerms.permission)
      )
    },
    canRequestRepoActivation(): boolean {
      return this.$gateKeeper.repo(RepoPerms.READ_REPO, this.repoPerms.permission)
    }
  },
  methods: {
    handleActivateClick(): void {
      // Activates directly in both modes. The parent's `activateAnalysis` calls
      // ToggleRepositoryActivation, which sets the analysis toggle and activates
      // regardless of whether the team commits a .deepsource.toml.
      this.$emit('activate-analysis')
    }
  }
})
